/**
 * Sector-Specific Content Configuration
 *
 * Provides customized "Why Board Champions?" section content per sector.
 * Falls back to global default if no sector match or content not defined.
 *
 * Updated: January 2025
 * Content reflects 2024-2025 UK board governance priorities, regulatory changes, and sector-specific challenges.
 */

export interface SectorResource {
  label: string
  href: string
  site: string
}

export interface SectorStats {
  leftTopLabel?: string
  leftTopValue?: string
  rightTopLabel?: string
  rightTopValue?: string
  leftBottomLabel?: string
  leftBottomValue?: string
  rightBottomLabel?: string
  rightBottomValue?: string
}

export interface SectorContext {
  id: string
  hEyebrow: string
  hTitle: string
  hSubtitle: string
  primaryCTAs: Array<{ label: string; href: string }>
  statsOverride?: SectorStats
  bodyParas: string[]
  resources: SectorResource[]
}

/**
 * Sector-specific content configurations
 */
export const SECTOR_CONTENT: Record<string, SectorContext> = {
  engineering: {
    id: 'engineering',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Engineering leadership that bridges technical depth with boardroom clarity.',
    hSubtitle: 'We connect scaling firms with Non-Executive Directors, CTOs and Engineering Directors who bring systems thinking, safety governance, and delivery discipline to manage technical complexity and accelerate programmes.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'UK boards face a critical technical skills gap—only 2% of FTSE 350 companies have technology specialists in the boardroom, yet engineering decisions now define competitive advantage, safety outcomes, and investor confidence. Our network includes former <strong>Group Engineers</strong>, <strong>Technical Directors</strong> and operations leaders across civil, mechanical, electrical, energy and advanced manufacturing who bring practical experience in <strong>systems engineering</strong>, <strong>Design for Manufacture and Assembly (DfMA)</strong>, and <strong>Building Information Modelling (BIM)</strong> governance at scale.',
      'Whether you are preparing for investor due diligence, navigating supplier re-tenders, managing complex <strong>EPC frameworks</strong>, or strengthening <strong>CDM compliance</strong> and safety governance, we place engineering leaders who translate technical roadmaps into board-level insight, ensuring engineering risk, capacity and delivery are understood, monitored and aligned to business strategy without lost momentum.'
    ],
    resources: [
      {
        label: 'Royal Academy of Engineering – Engineering & Governance',
        href: 'https://raeng.org.uk/',
        site: 'raeng.org.uk'
      },
      {
        label: 'IET – Institution of Engineering & Technology',
        href: 'https://www.theiet.org/',
        site: 'theiet.org'
      },
      {
        label: 'ICE – Institution of Civil Engineers',
        href: 'https://www.ice.org.uk/',
        site: 'ice.org.uk'
      },
      {
        label: 'UK Government – The Construction Playbook',
        href: 'https://www.gov.uk/government/publications/the-construction-playbook',
        site: 'gov.uk'
      }
    ]
  },

  financial: {
    id: 'financial',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Finance leadership that navigates scrutiny, growth and regulatory change.',
    hSubtitle: 'We connect high-growth and PE-backed firms with CFOs, Finance Directors and Audit Committee Chairs who bring institutional rigour, fundraising experience, compliance expertise and exit readiness.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'Financial governance separates companies that scale from those that stall. Regulatory intensity is rising—the <strong>FCA Consumer Duty</strong> remains the regulator\'s primary focus through 2025, <strong>operational resilience</strong> deadlines passed in March 2025, and <strong>Basel 3.1</strong> standards take effect in January 2027. Our network includes <strong>CFOs</strong> and <strong>Finance Directors</strong> who have led <strong>IPO readiness</strong>, vendor due diligence, management buy-outs and consolidation plays, bringing deep experience in <strong>FP&A transformation</strong>, working capital optimisation, covenant management, and audit committee discipline.',
      'Whether you are preparing for institutional investment, building board-level reporting, managing <strong>matching adjustment attestations</strong> for insurers, or navigating regulatory change (<strong>FCA requirements</strong>, <strong>Solvency UK</strong>, <strong>ESG disclosure</strong>), we place financial leaders who translate complexity into clarity, protect investor confidence, and build the control frameworks that withstand growth and regulatory scrutiny alike.'
    ],
    resources: [
      {
        label: 'FCA – Financial Conduct Authority',
        href: 'https://www.fca.org.uk/',
        site: 'fca.org.uk'
      },
      {
        label: 'ICAEW – Corporate Governance for CFOs',
        href: 'https://www.icaew.com/',
        site: 'icaew.com'
      },
      {
        label: 'FRC – UK Corporate Governance Code',
        href: 'https://www.frc.org.uk/',
        site: 'frc.org.uk'
      },
      {
        label: 'PRA – Prudential Regulation Authority',
        href: 'https://www.bankofengland.co.uk/prudential-regulation',
        site: 'bankofengland.co.uk'
      }
    ]
  },

  construction: {
    id: 'construction',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Construction governance built on safety, delivery discipline and financial control.',
    hSubtitle: 'We connect contractors, developers and infrastructure firms with Non-Execs, COOs and Project Directors who bring CAPEX steering, Building Safety Regulator compliance, and programme assurance expertise.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'The UK construction sector operates under heightened scrutiny following the Grenfell Tower disaster. The <strong>Building Safety Regulator</strong> framework took full effect in April 2024, introducing mandatory governance for higher-risk buildings, and the September 2024 Grenfell Inquiry report issued 58 recommendations reshaping safety procedures across the industry. With construction firms representing 16.2% of all UK insolvencies and over 4,260 companies becoming insolvent in 2024 alone, boards face intense financial, regulatory and reputational risk. Our network includes former <strong>Operations Directors</strong>, <strong>Quantity Surveyors</strong> and <strong>Project Delivery Leaders</strong> who have steered major infrastructure programmes, embedded <strong>CDM compliance</strong>, and introduced value engineering frameworks across civils, M&E and fit-out portfolios.',
      'Whether you are preparing for tier-one framework submissions, navigating <strong>JCT contract</strong> disputes, strengthening health and safety governance ahead of due diligence, or managing regulatory compliance with the <strong>National Infrastructure and Strategic Transport Authority (NISTA)</strong> launched April 2025, we place construction leaders who understand stage gates, cost certainty and board-level reporting in a sector where delivery defines reputation and survival.'
    ],
    resources: [
      {
        label: 'HSE – Building Safety Regulator',
        href: 'https://www.hse.gov.uk/building-safety/',
        site: 'hse.gov.uk'
      },
      {
        label: 'CIOB – Chartered Institute of Building',
        href: 'https://www.ciob.org/',
        site: 'ciob.org'
      },
      {
        label: 'RICS – Royal Institution of Chartered Surveyors',
        href: 'https://www.rics.org/',
        site: 'rics.org'
      },
      {
        label: 'Construction Leadership Council',
        href: 'https://www.constructionleadershipcouncil.co.uk/',
        site: 'constructionleadershipcouncil.co.uk'
      }
    ]
  },

  'human-resources': {
    id: 'human-resources',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'HR leadership that balances people strategy, compliance and commercial delivery.',
    hSubtitle: 'We connect scaling organisations with HR Directors, Chief People Officers and Employee Relations leaders who build compliant, high-performance cultures whilst managing workforce risk and strategic planning.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'The CHRO role has evolved from administrative HR to strategic enterprise leadership—boards now expect CHROs to drive business strategy, workforce risk oversight, organisational transformation and governance, particularly as <strong>generational workforce shifts</strong> and <strong>AI transformation</strong> reshape human capital management. Yet UK employee engagement remains critically low at just 20%, and 61% of CHROs report workforce planning restricted to one-year horizons. Our network includes <strong>HR Directors</strong> and <strong>Chief People Officers</strong> who have designed reward frameworks, led <strong>TUPE transfers</strong>, managed tribunal risk, and implemented <strong>diversity, equity and inclusion (DE&I)</strong> initiatives that deliver measurable retention, engagement and productivity improvements.',
      'Whether you are preparing for rapid headcount growth, managing restructures or redundancy programmes, strengthening employment policies ahead of <strong>Worker Protection Act</strong> requirements, or building data-driven workforce analytics and strategic workforce planning capability, we place HR professionals who understand both compliance and culture, translating people data into enterprise strategy and boardroom confidence in a talent landscape defined by scarcity and scrutiny.'
    ],
    resources: [
      {
        label: 'CIPD – Chartered Institute of Personnel & Development',
        href: 'https://www.cipd.org.uk/',
        site: 'cipd.org.uk'
      },
      {
        label: 'ACAS – Advisory, Conciliation and Arbitration Service',
        href: 'https://www.acas.org.uk/',
        site: 'acas.org.uk'
      },
      {
        label: 'EHRC – Equality and Human Rights Commission',
        href: 'https://www.equalityhumanrights.com/',
        site: 'equalityhumanrights.com'
      },
      {
        label: 'HSE – Workplace Health & Safety',
        href: 'https://www.hse.gov.uk/',
        site: 'hse.gov.uk'
      }
    ]
  },

  'artificial-intelligence': {
    id: 'artificial-intelligence',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'AI leadership that balances innovation, governance and regulatory preparedness.',
    hSubtitle: 'We place Chief AI Officers, ML Directors and Data Science leaders who have productionised machine learning, built responsible AI frameworks, and navigated the UK\'s principles-based regulatory approach.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'AI governance has moved from emerging risk to core board responsibility—nearly half of Fortune 100 companies now cite AI risk as part of board oversight (a threefold increase from 16% to 48% year-on-year), and the UK government published its comprehensive <strong>AI Playbook</strong> in February 2025, with legislation planned for 2025 making voluntary AI agreements legally binding. The proposed <strong>AI Authority</strong> would require companies to appoint dedicated <strong>AI Officers</strong> responsible for safe, ethical AI deployment. Our network includes <strong>Chief AI Officers</strong> and <strong>ML/AI Directors</strong> who have built and scaled machine learning platforms, navigated model risk management, and implemented <strong>responsible AI principles</strong> covering bias mitigation, explainability, transparency and data protection compliance.',
      'Whether you are operationalising large language models, preparing for the <strong>UK AI Regulation Bill</strong> and cross-sectoral principles enforcement, building AI governance committees, or managing stakeholder concerns around <strong>algorithmic accountability</strong> and <strong>automated decision-making</strong>, we connect you with AI practitioners who understand both the technical stack (<strong>MLOps</strong>, <strong>model monitoring</strong>, <strong>LLMOps</strong>) and the boardroom questions around risk, ethics, regulatory exposure and competitive advantage in an AI-accelerated economy. Information taken from the <a href="https://ai-speakers-agency.com/" target="_blank" rel="noopener noreferrer" style="color: #7294c7; text-decoration: underline;">AI Speakers Agency</a> - The world\'s leading Artificial Intelligence keynote speakers Bureau and data from the <a href="https://champions-speakers.co.uk/news/very-best-artificial-intelligence-speakers" target="_blank" rel="noopener noreferrer" style="color: #7294c7; text-decoration: underline;">Champions Speakers agency</a> who compiled the official best AI speakers in the world.'
    ],
    resources: [
      {
        label: 'UK Government – AI Playbook for Government',
        href: 'https://www.gov.uk/government/publications/ai-playbook-for-the-uk-government',
        site: 'gov.uk'
      },
      {
        label: 'Alan Turing Institute – Responsible AI Research',
        href: 'https://www.turing.ac.uk/',
        site: 'turing.ac.uk'
      },
      {
        label: 'ICO – AI and Data Protection Guidance',
        href: 'https://ico.org.uk/',
        site: 'ico.org.uk'
      },
      {
        label: 'UK AI Safety Institute',
        href: 'https://www.gov.uk/government/organisations/ai-safety-institute',
        site: 'gov.uk'
      }
    ]
  },

  ai: {
    id: 'ai',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'AI leadership that balances innovation, governance and regulatory preparedness.',
    hSubtitle: 'We place Chief AI Officers, ML Directors and Data Science leaders who have productionised machine learning, built responsible AI frameworks, and navigated the UK\'s principles-based regulatory approach.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'AI governance has moved from emerging risk to core board responsibility—nearly half of Fortune 100 companies now cite AI risk as part of board oversight (a threefold increase from 16% to 48% year-on-year), and the UK government published its comprehensive <strong>AI Playbook</strong> in February 2025, with legislation planned for 2025 making voluntary AI agreements legally binding. The proposed <strong>AI Authority</strong> would require companies to appoint dedicated <strong>AI Officers</strong> responsible for safe, ethical AI deployment. Our network includes <strong>Chief AI Officers</strong> and <strong>ML/AI Directors</strong> who have built and scaled machine learning platforms, navigated model risk management, and implemented <strong>responsible AI principles</strong> covering bias mitigation, explainability, transparency and data protection compliance.',
      'Whether you are operationalising large language models, preparing for the <strong>UK AI Regulation Bill</strong> and cross-sectoral principles enforcement, building AI governance committees, or managing stakeholder concerns around <strong>algorithmic accountability</strong> and <strong>automated decision-making</strong>, we connect you with AI practitioners who understand both the technical stack (<strong>MLOps</strong>, <strong>model monitoring</strong>, <strong>LLMOps</strong>) and the boardroom questions around risk, ethics, regulatory exposure and competitive advantage in an AI-accelerated economy. Information taken from the <a href="https://ai-speakers-agency.com/" target="_blank" rel="noopener noreferrer" style="color: #7294c7; text-decoration: underline;">AI Speakers Agency</a> - The world\'s leading Artificial Intelligence keynote speakers Bureau and data from the <a href="https://champions-speakers.co.uk/news/very-best-artificial-intelligence-speakers" target="_blank" rel="noopener noreferrer" style="color: #7294c7; text-decoration: underline;">Champions Speakers agency</a> who compiled the official best AI speakers in the world.'
    ],
    resources: [
      {
        label: 'UK Government – AI Playbook for Government',
        href: 'https://www.gov.uk/government/publications/ai-playbook-for-the-uk-government',
        site: 'gov.uk'
      },
      {
        label: 'Alan Turing Institute – Responsible AI Research',
        href: 'https://www.turing.ac.uk/',
        site: 'turing.ac.uk'
      },
      {
        label: 'ICO – AI and Data Protection Guidance',
        href: 'https://ico.org.uk/',
        site: 'ico.org.uk'
      },
      {
        label: 'UK AI Safety Institute',
        href: 'https://www.gov.uk/government/organisations/ai-safety-institute',
        site: 'gov.uk'
      }
    ]
  },

  technology: {
    id: 'technology',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Technology leadership that scales systems, manages cyber risk and delivers transformation.',
    hSubtitle: 'We place CTOs, CIOs and Technology Directors who have built platforms, led cloud migrations, and embedded DevOps, security and data governance at scale whilst navigating legacy modernisation challenges.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'Technology strategy is now business strategy, yet boards face a critical leadership gap—only 4 UK government departments have a digital leader on their executive committee, and legacy technology now comprises 28% of central government systems (up from 26% in 2023), driving 25% of organisations to suffer critical outages in 2024 alone. The UK launched a <strong>Cyber Governance Code of Practice</strong> in April 2025 to help boards manage digital risks and cyber threats, whilst cyber risk to the public sector remains critically high. Our network includes <strong>CTOs</strong> and <strong>CIOs</strong> who have scaled engineering teams from 10 to 200+, led platform re-architectures from monolith to <strong>microservices</strong>, and navigated cloud-native infrastructure deployments (<strong>AWS</strong>, <strong>Azure</strong>, <strong>GCP</strong>) alongside technical due diligence and product-engineering alignment.',
      'Whether you are facing performance bottlenecks, strengthening cyber security compliance (<strong>ISO 27001</strong>, <strong>SOC 2</strong>, <strong>Cyber Essentials</strong>, <strong>NIS2 Directive</strong>), managing legacy system modernisation, or preparing for technical due diligence ahead of fundraising, we place technology leaders who translate technical roadmaps into board-level clarity, ensuring technology investment, risk and capacity are understood, governed and aligned to growth without costly wrong turns.'
    ],
    resources: [
      {
        label: 'NCSC – National Cyber Security Centre',
        href: 'https://www.ncsc.gov.uk/',
        site: 'ncsc.gov.uk'
      },
      {
        label: 'UK Government – Cyber Governance Code of Practice',
        href: 'https://www.gov.uk/government/publications/cyber-governance-code-of-practice',
        site: 'gov.uk'
      },
      {
        label: 'BCS – The Chartered Institute for IT',
        href: 'https://www.bcs.org/',
        site: 'bcs.org'
      },
      {
        label: 'techUK – UK Technology Trade Association',
        href: 'https://www.techuk.org/',
        site: 'techuk.org'
      }
    ]
  },

  people: {
    id: 'people',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'People transformation leaders who embed culture, build capability and drive engagement.',
    hSubtitle: 'We connect scaling organisations with Chief People Officers, Culture Directors and Organisational Development leaders who architect behaviour, align people strategy to business outcomes, and rebuild trust.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'The revised <strong>UK Corporate Governance Code 2024</strong> (effective January 2025) requires boards to assess and monitor how desired culture has been embedded, yet UK employee engagement remains alarmingly low—only 20% of UK employees feel engaged, with 90% reporting disengagement and 23% actively seeking new roles. Research shows 86% of leaders agree that greater organisational transparency drives workforce trust. Our network includes <strong>Chief People Officers</strong> and <strong>Culture Directors</strong> who have led organisational redesigns, built talent pipelines from graduate to C-suite, introduced <strong>behavioural frameworks</strong>, <strong>engagement models</strong> and <strong>succession planning</strong> disciplines, and navigated post-acquisition integrations that protect culture whilst delivering synergies.',
      'Whether you are embedding the <strong>UK Corporate Governance Code</strong> culture requirements, building an <strong>Employee Value Proposition (EVP)</strong> to compete for scarce talent, strengthening <strong>diversity, inclusion and equal opportunity</strong> governance, or designing leadership development programmes and agile working models that support hypergrowth, we place people leaders who operate at the intersection of culture, capability and commercial delivery, ensuring your organisation scales without losing trust, purpose or what makes it distinctive in a talent-constrained economy.'
    ],
    resources: [
      {
        label: 'FRC – UK Corporate Governance Code 2024',
        href: 'https://www.frc.org.uk/',
        site: 'frc.org.uk'
      },
      {
        label: 'CIPD – Culture & Organisational Development',
        href: 'https://www.cipd.org.uk/',
        site: 'cipd.org.uk'
      },
      {
        label: 'ACAS – Employee Engagement & Culture Guidance',
        href: 'https://www.acas.org.uk/',
        site: 'acas.org.uk'
      },
      {
        label: 'Investors in People – Workplace Culture Standards',
        href: 'https://www.investorsinpeople.com/',
        site: 'investorsinpeople.com'
      }
    ]
  },

  marketing: {
    id: 'marketing',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Marketing leadership that connects brand strategy to measurable commercial outcomes.',
    hSubtitle: 'We place Chief Marketing Officers, Brand Directors and Growth Marketing leaders who have built category positions, driven demand generation, and delivered ROI-driven campaigns that translate marketing spend into pipeline and revenue.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'CMOs face intense scrutiny—average tenure remains just 4.2 years (trailing broader C-suite averages), and many boards still view marketing as a cost center rather than a growth driver. Only 16% of CMOs invest time building creative strategies linked to business outcomes, yet boards increasingly demand marketing leaders speak the CFO\'s language, moving from qualitative brand metrics to quantifiable data connecting activities to financial value. Our network includes <strong>Chief Marketing Officers</strong> and <strong>Growth Marketing Directors</strong> who have repositioned brands, launched products into new markets, built integrated demand generation engines, and driven <strong>AI adoption</strong> across martech stacks to deliver efficiency, measurement and growth outcomes boards demand.',
      'Whether you are preparing for a rebrand, building performance marketing functions, aligning sales and marketing through <strong>Account-Based Marketing (ABM)</strong> frameworks, navigating <strong>ASA advertising regulations</strong> and compliance requirements, or demonstrating marketing ROI and attribution across digital channels, we place marketing strategists who understand both creative excellence and commercial accountability, translating marketing investment into pipeline, revenue and shareholder value that earns boardroom credibility and secures budget in competitive growth environments.'
    ],
    resources: [
      {
        label: 'CIM – Chartered Institute of Marketing',
        href: 'https://www.cim.co.uk/',
        site: 'cim.co.uk'
      },
      {
        label: 'ASA – Advertising Standards Authority',
        href: 'https://www.asa.org.uk/',
        site: 'asa.org.uk'
      },
      {
        label: 'IPA – Institute of Practitioners in Advertising',
        href: 'https://ipa.co.uk/',
        site: 'ipa.co.uk'
      },
      {
        label: 'DMA – Data & Marketing Association',
        href: 'https://dma.org.uk/',
        site: 'dma.org.uk'
      }
    ]
  },

  digital: {
    id: 'digital',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Digital leadership that accelerates transformation, modernises systems and delivers user value.',
    hSubtitle: 'We place Chief Digital Officers, Digital Transformation Directors and eCommerce leaders who have modernised legacy infrastructure, scaled digital products, and embedded data-driven decision-making across organisations.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'Digital transformation remains elusive—only 22% of senior civil servants consider their departments "fully digital", and just 48% believe their organisation\'s digital priorities align with government\'s 2022-2025 roadmap. The UK launched its <strong>Digital Development Strategy 2024-2030</strong> focusing on inclusive, responsible and sustainable transformation, whilst regional digital ecosystem development could add £41.5 billion to the digital sector by 2025. Our network includes <strong>Chief Digital Officers</strong> and <strong>Digital Transformation Directors</strong> who have migrated on-premise infrastructure to cloud, led <strong>omnichannel commerce</strong> builds, implemented <strong>CRM</strong> and marketing automation platforms, established digital governance frameworks balancing innovation with information security, <strong>WCAG accessibility</strong> standards and data protection compliance.',
      'Whether you are digitising customer journeys, implementing low-code automation, preparing for <strong>cyber security certification</strong>, building internal digital capability and <strong>agile delivery</strong> teams, or managing complex legacy system retirement, we connect you with digital strategists who have delivered transformation at pace in regulated and fast-growth environments, translating technology investment into user adoption, operational efficiency, cost savings and competitive differentiation that withstands board scrutiny and market pressure.'
    ],
    resources: [
      {
        label: 'UK Government – Digital Strategy & Transformation',
        href: 'https://www.gov.uk/government/organisations/government-digital-service',
        site: 'gov.uk'
      },
      {
        label: 'BCS – British Computer Society',
        href: 'https://www.bcs.org/',
        site: 'bcs.org'
      },
      {
        label: 'techUK – Digital Transformation & Policy',
        href: 'https://www.techuk.org/',
        site: 'techuk.org'
      },
      {
        label: 'CDDO – Central Digital & Data Office',
        href: 'https://www.gov.uk/government/organisations/central-digital-and-data-office',
        site: 'gov.uk'
      }
    ]
  },

  communications: {
    id: 'communications',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Communications leadership that protects reputation and builds stakeholder trust.',
    hSubtitle: 'We place Communications Directors, PR leaders and Public Affairs advisors who have managed corporate narratives, crisis response, and stakeholder engagement through regulatory scrutiny, media pressure and market volatility.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'Reputation is your most valuable intangible asset and your most vulnerable—the revised <strong>UK Corporate Governance Code 2024</strong> (effective January 2025) significantly expands stakeholder engagement requirements, demanding greater board accountability for stakeholder relationships, transparency and trust. Having PR and communications professionals at the board table is no longer a luxury but a strategic imperative for managing reputation risk, stakeholder relations and crisis preparedness. Our network includes <strong>Communications Directors</strong> and <strong>Corporate Affairs leaders</strong> who have steered <strong>IPO communications</strong>, managed activist investor engagement, navigated regulatory scrutiny and media crises, and built internal communications frameworks aligning leadership messaging with employee engagement during transformation.',
      'Whether you are preparing for sensitive announcements, strengthening <strong>issues management and crisis protocols</strong>, building public affairs capability ahead of policy change, managing brand reputation across earned, owned and social channels, or embedding stakeholder engagement frameworks required under the <strong>2024 Code</strong>, we place communications professionals who operate with both journalistic instinct and boardroom judgement, protecting reputation whilst supporting commercial and strategic goals in an environment where trust is earned slowly and lost instantly.'
    ],
    resources: [
      {
        label: 'CIPR – Chartered Institute of Public Relations',
        href: 'https://www.cipr.co.uk/',
        site: 'cipr.co.uk'
      },
      {
        label: 'PRCA – Public Relations & Communications Association',
        href: 'https://www.prca.org.uk/',
        site: 'prca.org.uk'
      },
      {
        label: 'Ofcom – UK Communications Regulator',
        href: 'https://www.ofcom.org.uk/',
        site: 'ofcom.org.uk'
      },
      {
        label: 'GCS – Government Communication Service',
        href: 'https://www.communications.gov.uk/',
        site: 'communications.gov.uk'
      }
    ]
  },

  legal: {
    id: 'legal',
    hEyebrow: 'WHY BOARD CHAMPIONS?',
    hTitle: 'Legal leadership that manages regulatory risk, compliance and corporate governance.',
    hSubtitle: 'We place General Counsel, Chief Legal Officers and Compliance Directors who have navigated complex regulatory landscapes, built governance frameworks, and protected organisations through scrutiny, change and growth.',
    primaryCTAs: [], // No CTAs on sector pages
    bodyParas: [
      'Regulatory compliance has become the top risk for 41% of general counsel (an 11-percentage-point increase), reflecting the expanding global regulatory landscape with data privacy, AI governance and ESG emerging as critical focus areas. The <strong>UK Corporate Governance Code 2024</strong> introduces <strong>Provision 29</strong> (effective January 2026) requiring boards to monitor risk management and internal control frameworks, provide annual reviews covering all material controls including financial, operational, reporting and compliance, with specific declarations of effectiveness. General Counsel roles have evolved beyond legal advisors to strategic enterprise leaders overseeing corporate governance, ethics, regulatory compliance and executive decision-making. Our network includes <strong>General Counsel</strong> and <strong>Chief Legal Officers</strong> who have built legal and compliance functions, managed <strong>data privacy</strong> demands (consuming 61% of legal teams\' time), navigated incident response including data breaches, and addressed <strong>greenwashing risks</strong> taking on increasingly regulatory nature.',
      'Whether you are preparing for <strong>Provision 29</strong> compliance requirements, building board-level risk and compliance reporting, navigating sector-specific regulation (FCA, ICO, CMA, HSE), managing intellectual property portfolios, or strengthening corporate governance frameworks ahead of fundraising, acquisition or IPO, we place legal leaders who translate regulatory complexity into practical governance, protect the organisation from regulatory exposure, and provide the strategic counsel boards need to balance growth ambition with legal, ethical and reputational risk in a heightened-scrutiny environment.'
    ],
    resources: [
      {
        label: 'Law Society – Corporate & Commercial Law',
        href: 'https://www.lawsociety.org.uk/',
        site: 'lawsociety.org.uk'
      },
      {
        label: 'SRA – Solicitors Regulation Authority',
        href: 'https://www.sra.org.uk/',
        site: 'sra.org.uk'
      },
      {
        label: 'FRC – UK Corporate Governance Code',
        href: 'https://www.frc.org.uk/',
        site: 'frc.org.uk'
      },
      {
        label: 'ICO – Information Commissioner\'s Office',
        href: 'https://ico.org.uk/',
        site: 'ico.org.uk'
      }
    ]
  }
}

/**
 * Global default content (fallback when no sector or sector content not defined)
 */
export const GLOBAL_DEFAULT_CONTENT: SectorContext = {
  id: 'default',
  hEyebrow: 'WHY BOARD CHAMPIONS?',
  hTitle: 'Appoint your next Board-Level Advisor',
  hSubtitle: '<strong>BoardChampions</strong> work with the world\'s most influential industry-specific specialists, and influential leaders. A family-run business <strong>appointing over 2,000 experts</strong> across <strong>66 countries worldwide</strong>, we place top-class business talent at your fingertips.',
  primaryCTAs: [
    { label: 'Read our story', href: '/about' },
    { label: 'Discover Champions (UK) plc', href: 'https://championsukplc.com/' }
  ],
  bodyParas: [
    'We were named 57th in <strong>The Sunday Times\' Fast Track 100</strong> in association with Sir Richard Branson, and included in the <strong>London Stock Exchange\'s 1000 Companies</strong> to Inspire Britain.'
  ],
  resources: []
}

/**
 * Get sector content by slug or ID
 * Returns sector-specific content if available, otherwise returns global default
 *
 * @param sectorSlug - Sector identifier (e.g., 'engineering', 'financial', 'ai')
 * @returns SectorContext object with content
 */
export function getSectorContent(sectorSlug: string | null | undefined): SectorContext {
  if (!sectorSlug) return GLOBAL_DEFAULT_CONTENT

  const normalized = sectorSlug.toLowerCase().trim()

  // Check for exact match
  if (SECTOR_CONTENT[normalized]) {
    return SECTOR_CONTENT[normalized]
  }

  // Return global default if no match
  return GLOBAL_DEFAULT_CONTENT
}

/**
 * Check if a sector has custom content defined
 *
 * @param sectorSlug - Sector identifier
 * @returns True if custom content exists
 */
export function hasSectorContent(sectorSlug: string | null | undefined): boolean {
  if (!sectorSlug) return false
  const normalized = sectorSlug.toLowerCase().trim()
  return !!SECTOR_CONTENT[normalized]
}
